import { __rest } from "tslib";
import React, { useEffect, useRef, useState } from 'react';
import { createChildren } from '../Box/createChildren';
import { selectBirdElementsState, selectChildKeys, selectPromptHeaderState, } from '../../store/selectors';
import { connect, shallowEqual } from 'react-redux';
import { useDesignTokens, renderInlineCssVars } from '@sas/nova-commons/theme';
import { PromptHeaderState } from '../../store/promptHeader/promptHeaderState';
import { useI18n } from '../../locale/I18nContext';
import { useCustomReportAreaOverlay } from '../CustomOverlay';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { ArrowSmallDownBtn, ArrowSmallUpBtn } from '@sas/react-svg';
import { bindActionCreators } from 'redux';
import { ContextMenu } from '@sas/nova-commons/components/ContextMenu';
import { getUriParameterBoolean } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { getPagePromptHeaderId, ReportPromptHeaderId } from './utils';
import { PromptLayoutDataProvider } from '../PromptLayoutDataProvider';
import { BrowserName, getBrowserName } from '@sas/nova-commons/browser-name';
import { hsl, parseToHsl } from 'polished';
import cx from 'classnames';
import cn from './PromptHeader.css.json';
import './PromptHeader.css';
const editModeEnabled = getUriParameterBoolean('VASDK_PROMPT-HEADER-EDIT-MODE', false);
/**
 * dt.c.colorUiMedium, but safe for custom report themes
 */
function getHoverColor(dt) {
    const c = dt.reportTokens.c;
    switch (dt.themeVariant) {
        case 'high-contrast':
            return hsl(parseToHsl(c.neutral).hue, parseToHsl(c.neutral).saturation, 0.17);
        case 'dark':
            return hsl(parseToHsl(c.primary).hue, parseToHsl(c.primary).saturation, 0.15);
        default:
            return hsl(parseToHsl(c.neutral).hue, parseToHsl(c.neutral).saturation, 0.98);
    }
}
export const PromptHeaderBase = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const dt = useDesignTokens();
    const CustomReportAreaOverlay = useCustomReportAreaOverlay();
    const localize = useI18n();
    const status = props.status;
    const vertical = props.location === 'left' || props.location === 'right';
    // Whether the prompt header is in an orientation that is not at the top of the report
    const nonTopLocation = vertical || props.location === 'bottom';
    const hasContent = !!((_a = props.childKeys) === null || _a === void 0 ? void 0 : _a.length);
    const isOpen = status === 'open' || (status === 'auto' && hasContent);
    const viewMode = props.viewMode && !editModeEnabled;
    const isSingleArrow = props.type === 'report' &&
        props.hiddenNavigation &&
        (!viewMode || ((_b = props.pagePromptState) === null || _b === void 0 ? void 0 : _b.hasContent));
    const [hovering, setHovering] = useState(false);
    const contentContainer = useRef(null);
    const [transitionProps, setTransitionProps] = useState({ isOpen });
    const { open, close } = props.actions;
    // Used for toggling all control areas from within the report header area
    // Not used when the prompt header is a page header
    const pageHeaderStatus = (_c = props.pagePromptState) === null || _c === void 0 ? void 0 : _c.status;
    const pageHeaderIsOpen = pageHeaderStatus === 'open' ||
        (pageHeaderStatus === 'auto' && ((_d = props.pagePromptState) === null || _d === void 0 ? void 0 : _d.hasContent));
    const pageHeaderNonTopLocation = ['left', 'right', 'bottom'].includes(((_e = props.pagePromptState) === null || _e === void 0 ? void 0 : _e.location) || '');
    const content = !hasContent ? (React.createElement("span", { className: cx(cn.emptyText, vertical && cn.vertical) }, props.type === 'report'
        ? localize({ key: 'PromptHeader.emptyReport.txt' })
        : localize({ key: 'PromptHeader.emptyPage.txt' }))) : (React.createElement(PromptLayoutDataProvider, { direction: vertical ? 'vertical' : 'horizontal' }, createChildren(props.childKeys, props.elementState, {
        includePreferredSizing: true,
        style: !vertical
            ? {
                flexShrink: 1,
            }
            : undefined,
        contentJustification: 'center',
    })));
    let contextMenuSections;
    if (props.type === 'report') {
        if (props.hiddenNavigation) {
            contextMenuSections = {
                items: [
                    {
                        text: localize({ key: 'PromptHeader.allControlAreas.txt' }),
                        checked: isOpen && pageHeaderIsOpen,
                    },
                ],
            };
        }
        else {
            contextMenuSections = {
                items: [
                    { text: localize({ key: 'PromptHeader.reportControlArea.txt' }), checked: isOpen },
                    {
                        text: localize({ key: 'PromptHeader.allControlAreas.txt' }),
                        checked: isOpen && pageHeaderIsOpen,
                    },
                ],
            };
        }
    }
    else {
        contextMenuSections = {
            items: [{ text: localize({ key: 'PromptHeader.pageControlArea.txt' }), checked: isOpen }],
        };
    }
    const toggleStatus = (id, isChecked) => {
        isChecked ? open(id) : close(id);
    };
    let tooltipText;
    if (props.type === 'page') {
        tooltipText = isOpen
            ? localize({ key: 'PromptHeader.collapsePageControlArea.txt' })
            : localize({ key: 'PromptHeader.expandPageControlArea.txt' });
    }
    else {
        if (props.hiddenNavigation && !pageHeaderNonTopLocation) {
            tooltipText =
                isOpen && pageHeaderIsOpen
                    ? localize({ key: 'PromptHeader.collapseControlArea.txt' })
                    : localize({ key: 'PromptHeader.expandControlArea.txt' });
        }
        else {
            tooltipText = isOpen
                ? localize({ key: 'PromptHeader.collapseReportControlArea.txt' })
                : localize({ key: 'PromptHeader.expandReportControlArea.txt' });
        }
    }
    const controlArrow = (() => {
        var _a, _b;
        if (!props.hideArrow) {
            // Locations other than top center should not display an arrow
            if (nonTopLocation) {
                return null;
            }
            // In view mode, no arrows appear when controls are empty
            if (viewMode && !hasContent) {
                return null;
            }
            // With no page tab bar, page headers should not display an arrow
            if (props.hiddenNavigation &&
                props.type === 'page' &&
                // In view mode, page arrow is not hidden if there is no content in the report header
                !(viewMode && !((_a = props.reportPromptState) === null || _a === void 0 ? void 0 : _a.hasContent)) &&
                // If the report header is not at the top, the page arrow should not be hidden
                !['left', 'right', 'bottom'].includes((_b = props.reportPromptState) === null || _b === void 0 ? void 0 : _b.location)) {
                return null;
            }
            return (React.createElement(ContextMenu, { sections: [contextMenuSections], onItemSelect: (indices) => {
                    var _a;
                    for (const [, itemIndex] of indices) {
                        const isChecked = !contextMenuSections.items[itemIndex].checked;
                        toggleStatus(props.id, isChecked);
                        if (props.type === 'report' &&
                            ((_a = props.pagePromptState) === null || _a === void 0 ? void 0 : _a.id) &&
                            (itemIndex === 1 || props.hiddenNavigation)) {
                            toggleStatus(props.pagePromptState.id, isChecked);
                        }
                        contextMenuSections.items[itemIndex].checked = isChecked;
                    }
                } },
                React.createElement("div", { onMouseEnter: () => {
                        setHovering(true);
                        if (props.hiddenNavigation && props.onHover) {
                            // Communicates hover state to the page header when there are no page tabs
                            props.onHover(true);
                        }
                    }, onMouseLeave: () => {
                        setHovering(false);
                        if (props.hiddenNavigation && props.onHover) {
                            props.onHover(false);
                        }
                    }, className: cn.buttonContainer },
                    React.createElement(Button, { icon: {
                            icon: 
                            // Displays a down arrow if the page header isn't open yet when navigation is hidden
                            isOpen && (!isSingleArrow || pageHeaderIsOpen)
                                ? ArrowSmallUpBtn
                                : ArrowSmallDownBtn,
                        }, variant: ButtonVariant.LightSmall, onPress: () => {
                            var _a, _b, _c;
                            if (isOpen) {
                                if (isSingleArrow && !pageHeaderNonTopLocation) {
                                    // When there is no page tab bar, the report arrow acts as a double arrow and opens/closes both prompt headers
                                    if (!pageHeaderIsOpen) {
                                        // When in a mixed expansion state, expand all headers first
                                        open(((_a = props.pagePromptState) === null || _a === void 0 ? void 0 : _a.id) || '');
                                    }
                                    else {
                                        close(((_b = props.pagePromptState) === null || _b === void 0 ? void 0 : _b.id) || '');
                                        close(props.id);
                                    }
                                }
                                else {
                                    close(props.id);
                                }
                            }
                            else {
                                open(props.id);
                                if (isSingleArrow && !pageHeaderNonTopLocation) {
                                    open(((_c = props.pagePromptState) === null || _c === void 0 ? void 0 : _c.id) || '');
                                }
                            }
                        }, tooltip: tooltipText, tokens: () => ({ bg: 'transparent' }), className: cn.button }))));
        }
        else {
            return null;
        }
    })();
    useEffect(() => {
        if (!contentContainer.current)
            return;
        if (isOpen === transitionProps.isOpen) {
            if (!vertical) {
                // Manage the final height of horizontal prompt header content
                if (isOpen) {
                    // The header is open and transition is ongoing
                    // Only set height to auto if the transition has already reached 0px height
                    if (contentContainer.current.style.height === '0px') {
                        contentContainer.current.style.height = 'auto';
                    }
                }
                else {
                    // The header is closed and the transition is complete
                    contentContainer.current.style.height = '0px';
                }
            }
            return;
        }
        let sectionStyle;
        // Inspired by nova's WindowShade animation and the FLIP technique
        // https://aerotwist.com/blog/flip-your-animations/
        if (vertical) {
            // Vertical prompt headers - opacity transition
            if (isOpen) {
                // Closed to Open transition
                // Removing display=none property to prepare for transition
                contentContainer.current.style.display = '';
                // Triggers a synchronous layout
                contentContainer.current.getBoundingClientRect();
                sectionStyle = { opacity: 1 };
            }
            else {
                // Open to Closed transition
                sectionStyle = { opacity: 0, display: undefined };
            }
        }
        else {
            // Horizontal prompt headers - height transition
            if (isOpen) {
                // Closed to Open transition
                // Measuring height of content when set to 'auto'
                contentContainer.current.style.height = 'auto';
                contentContainer.current.style.display = '';
                // Triggers a synchronous layout
                const height = contentContainer.current.clientHeight;
                // Close the section to start the transition on the next react render
                contentContainer.current.style.height = '0px';
                // Another synchronous layout
                // Using a function call to avoid JS minifier issues
                contentContainer.current.getBoundingClientRect();
                sectionStyle = { height };
            }
            else {
                // Open to Closed transition
                // Measuring height of content when set to 'auto'
                contentContainer.current.style.height = 'auto';
                const currentHeight = contentContainer.current.clientHeight;
                contentContainer.current.style.height = `${currentHeight}px`;
                // Synchronous layout so the height mutation takes effect
                contentContainer.current.getBoundingClientRect();
                sectionStyle = { height: 0, display: undefined };
            }
        }
        setTransitionProps({ isOpen, className: cn.transitioning, style: sectionStyle });
    }, [isOpen, transitionProps.isOpen, vertical]);
    return (React.createElement("div", { style: Object.assign(Object.assign({}, renderInlineCssVars({
            bg: props.reportPromptHeaderHasHover && isOpen && !nonTopLocation
                ? getHoverColor(dt)
                : (_f = props.tokenOverrides) === null || _f === void 0 ? void 0 : _f.call(props, dt).bg,
            'hover-bg': isOpen ? getHoverColor(dt) : (_g = props.tokenOverrides) === null || _g === void 0 ? void 0 : _g.call(props, dt).bg,
        }, 'prompt-header')), { width: vertical && isOpen ? props.width : undefined }), className: cx(cn.header, vertical && cn.vertical, hovering && cn.hover), "data-testid": props.id },
        controlArrow,
        React.createElement("div", { className: cx(cn.headerContent, vertical && cn.vertical, transitionProps === null || transitionProps === void 0 ? void 0 : transitionProps.className, 
            // VASDK-2396: save room for the vertical scrollbar in FF
            getBrowserName() === BrowserName.Firefox && cn.scrollbarFix), style: vertical
                ? Object.assign({ display: transitionProps.isOpen ? undefined : 'none', opacity: transitionProps.isOpen ? 1 : 0 }, transitionProps.style) : Object.assign({ visibility: transitionProps.isOpen ? undefined : 'hidden', height: transitionProps.isOpen ? 'auto' : 0, padding: props.type === 'page' && props.location === 'top'
                    ? `${props.padding} ${props.padding} 0`
                    : undefined }, transitionProps.style), ref: contentContainer, onTransitionEnd: () => {
                setTransitionProps({ isOpen });
            } }, content),
        !props.hiddenNavigation && props.type === 'report' && (React.createElement("div", { className: !transitionProps.isOpen ? cn.paddingVisible : cn.paddingHidden })),
        CustomReportAreaOverlay && React.createElement(CustomReportAreaOverlay, { id: props.id, type: "header" })));
};
export const PromptHeader = connect((state, props) => {
    var _a;
    const pageId = props.type === 'page'
        ? props.id
        : getPagePromptHeaderId(((_a = state.sections.byId[state.sections.currentSection || '']) === null || _a === void 0 ? void 0 : _a.rootContainerId) || '');
    return Object.assign(Object.assign({ childKeys: selectChildKeys(state, props.id), elementState: selectBirdElementsState(state) }, selectPromptHeaderState(state, props.id)), { reportPromptState: Object.assign({ id: ReportPromptHeaderId, hasContent: !!selectChildKeys(state, ReportPromptHeaderId) }, selectPromptHeaderState(state, ReportPromptHeaderId)), pagePromptState: Object.assign({ id: pageId, hasContent: !!selectChildKeys(state, pageId) }, selectPromptHeaderState(state, pageId)) });
}, (dispatch) => ({
    actions: bindActionCreators(PromptHeaderState.actions, dispatch),
}), undefined, {
    areStatePropsEqual: (next, prev) => {
        const { reportPromptState: nextReportPromptState, pagePromptState: nextPagePromptState } = next, nextRest = __rest(next, ["reportPromptState", "pagePromptState"]);
        const { reportPromptState, pagePromptState } = prev, rest = __rest(prev, ["reportPromptState", "pagePromptState"]);
        return (shallowEqual(nextRest, rest) &&
            shallowEqual(nextReportPromptState, reportPromptState) &&
            shallowEqual(nextPagePromptState, pagePromptState));
    },
})(PromptHeaderBase);
